import React, { useState, useEffect } from "react";
import { X, DollarSign, Truck, Loader2 } from "lucide-react";

export default function EditShippingCostModal({
  isOpen,
  onClose,
  shipment,
  batch = null,
  batchIndex = null,
  totalBatches = 1,
  po = null,
  currentCost = 0,
  onSave
}) {
  const [cost, setCost] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isBatch = batch && (totalBatches > 1 || batchIndex !== null);
  const displayShipmentNumber = batchIndex !== null ? batchIndex + 1 : 1;

  useEffect(() => {
    if (isOpen) {
      const initial = currentCost !== undefined && currentCost !== null && !isNaN(currentCost)
        ? String(currentCost)
        : "";
      setCost(initial);
      setError("");
      setSaving(false);
    }
  }, [isOpen, currentCost]);

  if (!isOpen || !shipment) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsedCost = cost === "" ? 0 : parseFloat(cost);
    if (isNaN(parsedCost) || parsedCost < 0) {
      setError("Please enter a valid, non-negative shipping amount.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      await onSave(parsedCost);
      onClose();
    } catch (err) {
      console.error("Error saving shipping cost:", err);
      setError(err.message || "Failed to update shipping cost.");
    } finally {
      setSaving(false);
    }
  };

  const items = batch?.items || po?.items || [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()} 
        style={{ width: "90%", maxWidth: "480px" }}
      >
        <div className="modal-header">
          <h3 style={{ display: "flex", alignItems: "center", gap: "0.5rem", margin: 0 }}>
            <DollarSign size={20} className="text-warning" />
            {isBatch 
              ? `Actual Shipping Cost - Shipment #${displayShipmentNumber}` 
              : "Actual Shipping Cost - Full Shipment"}
          </h3>
          <button type="button" className="modal-close" onClick={onClose} disabled={saving}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "1.5rem" }}>
          {error && (
            <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>
              {error}
            </div>
          )}

          {/* Shipment Details Box */}
          <div style={{
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            border: "1px solid var(--border-color)",
            borderRadius: "6px",
            padding: "0.85rem 1rem",
            marginBottom: "1.25rem",
            fontSize: "0.85rem",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.5rem"
          }}>
            <div>
              <span style={{ color: "var(--text-secondary)", display: "block", fontSize: "0.75rem" }}>EST / PO Number</span>
              <strong style={{ color: "var(--text-primary)" }}>{shipment.poNumber || po?.poNumber || "N/A"}</strong>
            </div>
            <div>
              <span style={{ color: "var(--text-secondary)", display: "block", fontSize: "0.75rem" }}>Client</span>
              <strong style={{ color: "var(--text-primary)" }}>{shipment.clientName || po?.clientName || "N/A"}</strong>
            </div>
            <div>
              <span style={{ color: "var(--text-secondary)", display: "block", fontSize: "0.75rem" }}>Carrier</span>
              <span style={{ color: "var(--text-primary)" }}>{batch?.carrier || shipment.carrier || "N/A"}</span>
            </div>
            <div>
              <span style={{ color: "var(--text-secondary)", display: "block", fontSize: "0.75rem" }}>Tracking #</span>
              <span style={{ color: "var(--text-primary)", wordBreak: "break-all" }}>{batch?.trackingNumber || shipment.trackingNumber || "N/A"}</span>
            </div>
            {batch?.shippedAt && (
              <div style={{ gridColumn: "span 2" }}>
                <span style={{ color: "var(--text-secondary)", display: "block", fontSize: "0.75rem" }}>Shipped Date</span>
                <span style={{ color: "var(--text-primary)" }}>{new Date(batch.shippedAt).toLocaleDateString()}</span>
              </div>
            )}
          </div>

          <p style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginBottom: "1.25rem", lineHeight: 1.4 }}>
            Enter the total actual shipping amount invoiced for this shipment. This lumps the shipping cost per shipment rather than per line item.
          </p>

          {/* Actual Shipping Amount Input */}
          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label className="form-label" htmlFor="actualShippingCostInput" style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <Truck size={14} /> Actual Shipping Amount ($) *
            </label>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <span style={{ position: "absolute", left: "0.75rem", color: "var(--text-muted)", fontWeight: 600 }}>$</span>
              <input
                id="actualShippingCostInput"
                type="number"
                step="0.01"
                min="0"
                className="form-input"
                style={{ paddingLeft: "1.75rem", fontSize: "1rem", fontWeight: 600 }}
                placeholder="0.00"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                disabled={saving}
                autoFocus
                required
              />
            </div>
          </div>

          {/* Shipped Items Summary */}
          {items && items.length > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              <span style={{ fontSize: "0.75rem", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600, display: "block", marginBottom: "0.4rem" }}>
                Items in this shipment ({items.length})
              </span>
              <div style={{ maxHeight: "120px", overflowY: "auto", border: "1px solid var(--border-color)", borderRadius: "4px", padding: "0.4rem 0.6rem", fontSize: "0.8rem", backgroundColor: "rgba(0,0,0,0.1)" }}>
                {items.map((it, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "0.2rem 0", borderBottom: i < items.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
                    <span style={{ color: "var(--text-primary)" }}>{it.lineNumber ? `${it.lineNumber} - ` : ""}{it.name || it.description || "Item"}</span>
                    <span style={{ color: "var(--text-secondary)", fontWeight: 600 }}>Qty: {it.quantity || 1}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="modal-footer" style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.5rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
              {saving ? <Loader2 size={16} className="spinner-icon" /> : null}
              Save Shipping Cost
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
